import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { colisService } from '../services/colisService';
import toast from 'react-hot-toast';

interface FeedbackInfo {
  tracking_number: string;
  destinataire_nom: string;
  livreur_nom: string | null;
  already_rated: boolean;
}

const Star: React.FC<{ filled: boolean; onClick: () => void; onHover: () => void }> = ({ filled, onClick, onHover }) => (
  <button
    onClick={onClick}
    onMouseEnter={onHover}
    className={`text-4xl transition-transform hover:scale-110 focus:outline-none ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
    type="button"
  >
    ★
  </button>
);

export const FeedbackPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<FeedbackInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoverNote, setHoverNote] = useState(0);
  const [note, setNote] = useState(0);
  const [commentaire, setCommentaire] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    colisService.getFeedback(token)
      .then(setInfo)
      .catch(() => setError('Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (note === 0) { toast.error('Veuillez sélectionner une note'); return; }
    setSubmitting(true);
    try {
      await colisService.submitFeedback(token!, { note, commentaire: commentaire || undefined });
      setSubmitted(true);
      toast.success('Merci pour votre évaluation !');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">😕</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Lien invalide</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (info?.already_rated || submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Merci pour votre avis !</h1>
          <p className="text-gray-500">Votre évaluation a bien été enregistrée.</p>
        </div>
      </div>
    );
  }

  const displayNote = hoverNote || note;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">📦</span>
          </div>
          <h1 className="text-xl font-bold text-gray-800">Évaluez votre livraison</h1>
          <p className="text-gray-500 text-sm mt-1">
            Colis <span className="font-medium text-indigo-600">{info?.tracking_number}</span>
          </p>
          {info?.livreur_nom && (
            <p className="text-gray-500 text-sm">Livré par <span className="font-medium">{info.livreur_nom}</span></p>
          )}
        </div>

        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3 text-center">Quelle note donnez-vous à cette livraison ?</p>
          <div className="flex justify-center gap-1" onMouseLeave={() => setHoverNote(0)}>
            {[1, 2, 3, 4, 5].map(n => (
              <Star
                key={n}
                filled={n <= displayNote}
                onClick={() => setNote(n)}
                onHover={() => setHoverNote(n)}
              />
            ))}
          </div>
          {displayNote > 0 && (
            <p className="text-center text-sm text-gray-500 mt-2">
              {['', 'Très mauvais', 'Mauvais', 'Correct', 'Bien', 'Excellent !'][displayNote]}
            </p>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire (optionnel)</label>
          <textarea
            value={commentaire}
            onChange={e => setCommentaire(e.target.value)}
            placeholder="Partagez votre expérience..."
            className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400"
            rows={3}
            maxLength={500}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={submitting || note === 0}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-semibold py-3 rounded-xl transition-colors"
        >
          {submitting ? 'Envoi en cours...' : 'Envoyer mon évaluation'}
        </button>
      </div>
    </div>
  );
};
